// frontend/src/pages/ScheduledSubmissionsPage.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  TablePagination,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  List,
  ListItem,
  CardMedia,
  Divider
} from '@mui/material';
import { format, formatDistanceToNow, isPast, addDays, subDays } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import MergeIcon from '@mui/icons-material/MergeType';
import apiClient from '../api/client';
import { auditMissingBadgeRemovals, fixMissingBadgeRemovals, dismissBadgeAuditItem } from '../api/scheduler';
import BadgeRemovalReviewDialog from '../components/BadgeRemovalReviewDialog';
import { auditDuplicateImages, mergeDuplicateImages } from '../api/images';
import { useNavigate } from 'react-router-dom';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'completed_publish_failed', label: 'Completed (publish failed)' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const TYPE_OPTIONS = [
  { value: 'submission', label: 'Submission' },
  { value: 'badge_removal', label: 'Badge Removal' }
];

const getDefaultFilters = () => ({
  gtin_search: '',
  status: ['pending', 'completed', 'completed_publish_failed'],
  type: [],
  scheduled_after: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
  scheduled_before: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
  user_scope: 'all'
});

const ScheduledSubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [newScheduledDate, setNewScheduledDate] = useState(new Date());
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [filters, setFilters] = useState(getDefaultFilters);
  const [showHistory, setShowHistory] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [fixLoading, setFixLoading] = useState(false);
  const [dupAuditResult, setDupAuditResult] = useState(null);
  const [dupAuditLoading, setDupAuditLoading] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [selectedMergeGtins, setSelectedMergeGtins] = useState(new Set());
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchScheduledSubmissions();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/users/');
      const data = response.data;
      setUsersList(Array.isArray(data) ? data : (data?.users || data?.items || []));
    } catch (err) {
      console.warn('Failed to fetch users list:', err);
    }
  };

  // Client-side: only handle showHistory dedup (all other filtering is server-side)
  const filteredSubmissions = useMemo(() => {
    let result = [...submissions];

    // When showHistory is off, only keep the latest submission per image
    if (!showHistory) {
      const latestByImage = new Map();
      for (const sub of result) {
        if (!latestByImage.has(sub.image_id)) {
          latestByImage.set(sub.image_id, sub);
        }
      }
      result = Array.from(latestByImage.values());
    }

    return result;
  }, [submissions, showHistory]);

  // Reset pagination when filters change
  useEffect(() => {
    setPage(0);
  }, [showHistory]);

  const fetchScheduledSubmissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('all_users', 'true');
      params.append('limit', '5000');

      if (filters.status.length > 0) {
        params.append('status', filters.status.join(','));
      }
      if (filters.scheduled_after) {
        params.append('scheduled_after', filters.scheduled_after);
      }
      if (filters.scheduled_before) {
        params.append('scheduled_before', filters.scheduled_before);
      }
      if (filters.type.length === 1) {
        params.append('submission_type', filters.type[0]);
      }
      if (filters.gtin_search) {
        // Use first GTIN for server-side search
        const firstGtin = filters.gtin_search.split(/[\n\r,\t]+/)[0].trim();
        if (firstGtin) params.append('gtin_search', firstGtin);
      }

      const response = await apiClient.get(`/scheduler/scheduled?${params.toString()}`);
      setSubmissions(response.data);
      setPage(0);
    } catch (err) {
      setError('Failed to load scheduled submissions');
      console.error('Error fetching scheduled submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (submission) => {
    setSelectedSubmission(submission);
    setNewScheduledDate(new Date(submission.scheduled_for));
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (submission) => {
    setSelectedSubmission(submission);
    setDeleteDialogOpen(true);
  };

  const handleUpdateSubmission = async () => {
    if (!selectedSubmission) return;
    setActionLoading(true);
    try {
      await apiClient.put(`/scheduler/scheduled/${selectedSubmission.id}`, {
        scheduled_for: newScheduledDate
      });
      setSuccessMessage('Scheduled submission updated successfully');
      fetchScheduledSubmissions();
      setEditDialogOpen(false);
    } catch (err) {
      setError('Failed to update scheduled submission');
      console.error('Error updating submission:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubmission = async () => {
    if (!selectedSubmission) return;
    setActionLoading(true);
    try {
      await apiClient.delete(`/scheduler/scheduled/${selectedSubmission.id}`);
      setSuccessMessage('Scheduled submission cancelled successfully');
      fetchScheduledSubmissions();
      setDeleteDialogOpen(false);
    } catch (err) {
      setError('Failed to cancel scheduled submission');
      console.error('Error cancelling submission:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAuditBadgeRemovals = async () => {
    setAuditLoading(true);
    setError(null);
    try {
      const result = await auditMissingBadgeRemovals();
      setAuditResult(result);
      if (result.missing_count === 0 && result.failed_count === 0) {
        setSuccessMessage('All scheduled submissions have proper badge removals configured.');
      }
    } catch (err) {
      setError('Failed to audit badge removals');
      console.error('Error auditing badge removals:', err);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleFixBadgeRemovals = async () => {
    setFixLoading(true);
    setError(null);
    try {
      const result = await fixMissingBadgeRemovals();
      const parts = [];
      if (result.created_count > 0) parts.push(`Created ${result.created_count} missing badge removal(s)`);
      if (result.retried_count > 0) parts.push(`Retried ${result.retried_count} failed badge removal(s)`);
      setSuccessMessage(parts.join('. ') + '.');
      setAuditResult(null);
      fetchScheduledSubmissions();
    } catch (err) {
      setError('Failed to fix missing badge removals');
      console.error('Error fixing badge removals:', err);
    } finally {
      setFixLoading(false);
    }
  };

  const handleAuditDuplicates = async () => {
    setDupAuditLoading(true);
    setError(null);
    try {
      const result = await auditDuplicateImages();
      setDupAuditResult(result);
      if (result.duplicate_count === 0) {
        setSuccessMessage('No duplicate GTINs found.');
      } else {
        // Pre-select all GTINs for merge
        setSelectedMergeGtins(new Set(result.duplicates.map(d => d.gtin)));
        setMergeDialogOpen(true);
      }
    } catch (err) {
      setError('Failed to audit duplicates');
      console.error('Error auditing duplicates:', err);
    } finally {
      setDupAuditLoading(false);
    }
  };

  const handleMergeDuplicates = async () => {
    const gtinsToMerge = [...selectedMergeGtins];
    if (gtinsToMerge.length === 0) return;

    setMergeLoading(true);
    setError(null);
    try {
      const result = await mergeDuplicateImages(gtinsToMerge);
      setSuccessMessage(
        `Merged ${result.duplicates_merged} duplicate image(s) across ${result.gtins_processed} GTIN(s). ` +
        `${result.versions_absorbed} version(s) absorbed, ${result.submissions_transferred} submission(s) transferred.`
      );
      setDupAuditResult(null);
      setMergeDialogOpen(false);
      setSelectedMergeGtins(new Set());
      fetchScheduledSubmissions();
    } catch (err) {
      setError('Failed to merge duplicates');
      console.error('Error merging duplicates:', err);
    } finally {
      setMergeLoading(false);
    }
  };

  const handleApproveRemoval = async (item, reviewData) => {
    try {
      if (item.type === 'failed') {
        // Retry the failed submission
        await fixMissingBadgeRemovals();
      } else {
        // Schedule badge removal for the proper date
        // Use badge_removal_due from the review data, or fall back to now if overdue/unavailable
        let removalDate;
        if (reviewData?.badge_removal_due) {
          const dueDate = new Date(reviewData.badge_removal_due);
          // If the due date is in the past, schedule for now
          removalDate = dueDate > new Date() ? dueDate.toISOString() : new Date().toISOString();
        } else {
          removalDate = new Date().toISOString();
        }

        await apiClient.post(`/scheduler/schedule/${item.image_id}`, {
          scheduled_for: removalDate,
          create_badge_removal_suggestions: false,
          is_badge_removal: true,
          original_badges: item.badges || item.display_badges || []
        });
      }
      const scheduledDate = reviewData?.badge_removal_due
        ? new Date(reviewData.badge_removal_due).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : 'now';
      setSuccessMessage(`Badge removal approved for GTIN ${item.image_gtin}, scheduled for ${scheduledDate}`);
    } catch (err) {
      console.error('Error approving badge removal:', err);
      throw err;
    }
  };

  const handleReviewComplete = () => {
    setReviewDialogOpen(false);
    setAuditResult(null);
    fetchScheduledSubmissions();
    setSuccessMessage('Badge removal review complete.');
  };

  const toggleMergeGtin = (gtin) => {
    setSelectedMergeGtins(prev => {
      const next = new Set(prev);
      if (next.has(gtin)) next.delete(gtin);
      else next.add(gtin);
      return next;
    });
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'default', icon: <HourglassEmptyIcon fontSize="small" />, label: 'Pending' },
      processing: { color: 'warning', icon: <HourglassEmptyIcon fontSize="small" />, label: 'Processing' },
      completed: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Completed' },
      completed_publish_failed: { color: 'warning', icon: <ErrorIcon fontSize="small" />, label: 'Submitted (publish failed)' },
      failed: { color: 'error', icon: <ErrorIcon fontSize="small" />, label: 'Failed' },
      cancelled: { color: 'default', icon: <CancelIcon fontSize="small" />, label: 'Cancelled' }
    };
    const config = statusConfig[status] || { color: 'default', icon: null, label: status.charAt(0).toUpperCase() + status.slice(1) };
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ my: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            <ScheduleIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
            Scheduled Submissions
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={auditLoading ? <CircularProgress size={18} /> : <BuildIcon />}
              onClick={handleAuditBadgeRemovals}
              disabled={auditLoading}
            >
              {auditLoading ? 'Auditing...' : 'Audit Badge Removals'}
            </Button>
            <Button
              variant="outlined"
              color="info"
              startIcon={dupAuditLoading ? <CircularProgress size={18} /> : <MergeIcon />}
              onClick={handleAuditDuplicates}
              disabled={dupAuditLoading}
            >
              {dupAuditLoading ? 'Checking...' : 'Audit Duplicates'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchScheduledSubmissions}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Search-style filter panel */}
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="GTIN Search"
                placeholder="Enter GTIN(s) - comma separated or paste a list"
                value={filters.gtin_search}
                multiline
                minRows={1}
                maxRows={3}
                onChange={(e) => setFilters(prev => ({ ...prev, gtin_search: e.target.value }))}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = e.clipboardData.getData('text');
                  const cleaned = pasted
                    .split(/[\n\r,\t]+/)
                    .map(s => s.trim())
                    .filter(Boolean)
                    .join(', ');
                  setFilters(prev => {
                    const existing = prev.gtin_search ? prev.gtin_search.trim() : '';
                    const separator = existing && !existing.endsWith(',') ? ', ' : '';
                    return { ...prev, gtin_search: existing + separator + cleaned };
                  });
                }}
                helperText="Enter or paste GTINs (comma, newline, or tab separated)"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  input={<OutlinedInput label="Status" />}
                  renderValue={(selected) => selected.map(val => STATUS_OPTIONS.find(opt => opt.value === val)?.label || val).join(', ')}
                >
                  {STATUS_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={filters.status.indexOf(option.value) > -1} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  multiple
                  value={filters.type}
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                  input={<OutlinedInput label="Type" />}
                  renderValue={(selected) => selected.map(val => TYPE_OPTIONS.find(opt => opt.value === val)?.label || val).join(', ')}
                >
                  {TYPE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={filters.type.indexOf(option.value) > -1} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>User</InputLabel>
                <Select
                  value={filters.user_scope}
                  label="User"
                  onChange={(e) => setFilters(prev => ({ ...prev, user_scope: e.target.value }))}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  {usersList.map(u => (
                    <MenuItem key={u.id} value={u.id.toString()}>
                      {u.username} ({u.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Scheduled After"
                value={filters.scheduled_after}
                onChange={(e) => setFilters(prev => ({ ...prev, scheduled_after: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Scheduled Before"
                value={filters.scheduled_before}
                onChange={(e) => setFilters(prev => ({ ...prev, scheduled_before: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={fetchScheduledSubmissions}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  disabled={loading}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setFilters(getDefaultFilters())}
                >
                  Clear
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showHistory}
                      onChange={(e) => setShowHistory(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Show full history per asset</Typography>}
                />
                <Typography variant="caption" color="text.secondary">
                  Showing {filteredSubmissions.length} of {submissions.length} submissions
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {auditResult && (auditResult.missing_count > 0 || auditResult.failed_count > 0) && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            onClose={() => setAuditResult(null)}
            action={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  color="primary"
                  size="small"
                  variant="contained"
                  onClick={() => setReviewDialogOpen(true)}
                >
                  Review Each
                </Button>
                <Button
                  color="warning"
                  size="small"
                  variant="outlined"
                  onClick={handleFixBadgeRemovals}
                  disabled={fixLoading}
                  startIcon={fixLoading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {fixLoading ? 'Fixing...' : `Fix All (${auditResult.missing_count + auditResult.failed_count})`}
                </Button>
              </Box>
            }
          >
            {auditResult.missing_count > 0 && (
              <Box sx={{ mb: auditResult.failed_count > 0 ? 1.5 : 0 }}>
                <Typography variant="body2" fontWeight="bold">
                  {auditResult.missing_count} submission(s) missing badge removals:
                </Typography>
                {auditResult.submissions.slice(0, 5).map(s => (
                  <Typography key={s.submission_id} variant="caption" display="block" sx={{ mt: 0.5 }}>
                    GTIN {s.image_gtin}: {s.badges.join(', ')} (#{s.submission_id}, {s.status})
                  </Typography>
                ))}
                {auditResult.missing_count > 5 && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    ...and {auditResult.missing_count - 5} more
                  </Typography>
                )}
              </Box>
            )}
            {auditResult.failed_count > 0 && (
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {auditResult.failed_count} failed badge removal(s) to retry:
                </Typography>
                {auditResult.failed_removals.slice(0, 5).map(s => (
                  <Typography key={s.submission_id} variant="caption" display="block" sx={{ mt: 0.5 }}>
                    GTIN {s.image_gtin}: {(s.original_badges || []).join(', ')} &mdash; {s.error_message}
                  </Typography>
                ))}
                {auditResult.failed_count > 5 && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    ...and {auditResult.failed_count - 5} more
                  </Typography>
                )}
              </Box>
            )}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Image / GTIN</TableCell>
                <TableCell>Scheduled For</TableCell>
                <TableCell>Time Until</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Error</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSubmissions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Click to view full size">
                          <Box
                            component="img"
                            src={`${apiClient.defaults.baseURL}/images/${submission.image_id}/thumbnail?size=100&t=${new Date().getTime()}`}
                            alt={`MRHI for ${submission.image_gtin}`}
                            onClick={() => window.open(`${apiClient.defaults.baseURL}/images/${submission.image_id}/file?t=${new Date().getTime()}`, '_blank')}
                            sx={{
                              width: 40,
                              height: 40,
                              objectFit: 'contain',
                              cursor: 'pointer',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              '&:hover': {
                                borderColor: 'primary.main',
                                boxShadow: 1
                              }
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiIvPjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ii8+PHBvbHlsaW5lIHBvaW50cz0iMjEgMTUgMTYgMTAgNSAyMSIvPjwvc3ZnPg==';
                            }}
                          />
                        </Tooltip>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => navigate(`/images/${submission.image_id}`)}
                        >
                          {submission.image_gtin}
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {format(new Date(submission.scheduled_for), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell>
                      {submission.status === 'pending' ? (
                        isPast(new Date(submission.scheduled_for)) ? (
                          <Typography variant="body2" color="error">
                            Overdue
                          </Typography>
                        ) : (
                          formatDistanceToNow(new Date(submission.scheduled_for), { addSuffix: true })
                        )
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          &mdash;
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {submission.is_badge_removal ? (
                        <Chip
                          label="Badge Removal"
                          size="small"
                          icon={<LocalOfferIcon fontSize="small" />}
                          color="secondary"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="Submission"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>{getStatusChip(submission.status)}</TableCell>
                    <TableCell>
                      {submission.error_message ? (
                        <Tooltip title={submission.error_message}>
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{
                              maxWidth: 200,
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {submission.error_message}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">&mdash;</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {submission.status === 'pending' && (
                        <>
                          <Tooltip title="Edit schedule">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(submission)}
                              disabled={actionLoading}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel submission">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(submission)}
                              disabled={actionLoading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              {filteredSubmissions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" color="text.secondary" sx={{ py: 3 }}>
                      {submissions.length === 0
                        ? 'No scheduled submissions found'
                        : 'No submissions match the current filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredSubmissions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Scheduled Date</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                label="New scheduled date"
                type="datetime-local"
                value={newScheduledDate ? format(newScheduledDate, "yyyy-MM-dd'T'HH:mm") : ''}
                onChange={(e) => setNewScheduledDate(new Date(e.target.value))}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: format(new Date(), "yyyy-MM-dd'T'HH:mm") }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSubmission} variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Cancel Scheduled Submission</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to cancel this scheduled submission for GTIN {selectedSubmission?.image_gtin}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
              Keep
            </Button>
            <Button onClick={handleCancelSubmission} color="error" variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Cancel Submission'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Merge Duplicates Dialog */}
        <Dialog
          open={mergeDialogOpen}
          onClose={() => !mergeLoading && setMergeDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <MergeIcon color="info" />
              <Typography variant="h6">Merge Duplicate Images</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {dupAuditResult && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Found {dupAuditResult.duplicate_count} GTIN(s) with duplicate images.
                  Select the ones you want to merge. For each selected GTIN, the most recently
                  submitted image will be kept as the primary, and versions from duplicates will be absorbed.
                </Alert>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedMergeGtins(new Set(dupAuditResult.duplicates.map(d => d.gtin)))}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedMergeGtins(new Set())}
                  >
                    Deselect All
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
                    {selectedMergeGtins.size} of {dupAuditResult.duplicate_count} selected
                  </Typography>
                </Box>

                <List sx={{ maxHeight: 400, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {dupAuditResult.duplicates.map((dup, idx) => (
                    <React.Fragment key={dup.gtin}>
                      {idx > 0 && <Divider />}
                      <ListItem
                        sx={{
                          alignItems: 'flex-start',
                          bgcolor: selectedMergeGtins.has(dup.gtin) ? 'action.selected' : 'transparent'
                        }}
                      >
                        <Checkbox
                          checked={selectedMergeGtins.has(dup.gtin)}
                          onChange={() => toggleMergeGtin(dup.gtin)}
                          sx={{ mt: 0.5 }}
                        />
                        <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                          <CardMedia
                            component="img"
                            sx={{
                              width: 60,
                              height: 60,
                              objectFit: 'contain',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'grey.50'
                            }}
                            image={`${apiClient.defaults.baseURL}/images/${dup.images[0].image_id}/thumbnail?size=200`}
                            alt={`GTIN ${dup.gtin}`}
                            onError={(e) => { e.target.src = 'https://placehold.co/200x200/eee/ccc?text=No+Preview'; }}
                          />
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2">
                                GTIN: {dup.gtin} ({dup.count} copies)
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                {dup.images.map((img, i) => (
                                  <Typography key={img.image_id} variant="caption" display="block">
                                    {img.owner_username || img.owner_email}: {img.status}
                                    {img.is_submitted && <Chip label="submitted" size="small" color="success" variant="outlined" sx={{ ml: 0.5, height: 16, fontSize: '0.65rem' }} />}
                                    {img.pending_submissions > 0 && <Chip label={`${img.pending_submissions} scheduled`} size="small" color="warning" variant="outlined" sx={{ ml: 0.5, height: 16, fontSize: '0.65rem' }} />}
                                    {' '} ({img.version_count} version{img.version_count !== 1 ? 's' : ''})
                                  </Typography>
                                ))}
                              </Box>
                            }
                          />
                        </Box>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setMergeDialogOpen(false)}
              disabled={mergeLoading}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleMergeDuplicates}
              disabled={mergeLoading || selectedMergeGtins.size === 0}
              startIcon={mergeLoading ? <CircularProgress size={18} color="inherit" /> : <MergeIcon />}
            >
              {mergeLoading ? 'Merging...' : `Merge ${selectedMergeGtins.size} GTIN(s)`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Badge Removal Review Dialog */}
        <BadgeRemovalReviewDialog
          open={reviewDialogOpen}
          onClose={() => setReviewDialogOpen(false)}
          auditItems={auditResult}
          onApprove={handleApproveRemoval}
          onComplete={handleReviewComplete}
        />
      </Box>
    </Box>
  );
};

export default ScheduledSubmissionsPage;
